/**
 * /pair/:code — the phone half of board pairing.
 *
 * An admin scans the QR on a tablet's /board screen and lands here. The whole
 * point of this page is that THE VENUE COMES FROM THE ACCOUNT, not the URL:
 * the code identifies a device, the session identifies who is pairing it, and
 * their user_venue_roles rows say where they are allowed to. Nobody types or
 * guesses a venue slug.
 *
 * Reachable at /pair with no code too, where it asks for one. That is the
 * fallback /board offers when a camera will not focus across a dim room.
 *
 * GUARDED BY THE DEFAULT ProtectedRoute, NOT `venueAdmin`. That variant
 * resolves its venue from a :venueSlug route param, and this route has none —
 * there is no venue yet, which is the question being answered. The admin check
 * is the server's, in .../tournaments/:id/pair, which runs the existing
 * requireRole(..., 'venue_admin', venueId). Nothing here invents a permission
 * check; the venue list below only decides what to OFFER.
 *
 * Signing in first is handled by ProtectedRoute and Login between them — the
 * deep link survives via location.state.from, so a scan on a signed-out phone
 * goes login → back here.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../context/AuthContext.jsx';
import { apiGet, apiPost } from '../lib/api.js';
import { formatDateShort } from '../lib/format.js';

/** Mirrors ProtectedRoute — a platform role outranks any venue role. */
const PLATFORM_ROLES    = new Set(['platform_owner', 'super_admin']);
const VENUE_ADMIN_ROLES = new Set(['venue_admin', 'venue_owner']);

/**
 * Mirrors PAIRABLE_STATUSES in .../tournaments/[tournamentId]/pair.js. A board
 * is paired on the night or just before it; offering a draft or a finished
 * night would only earn a 409 from the endpoint.
 */
const PAIRABLE_STATUSES = ['registration', 'active'];

const TOKEN_PATTERN = /^[A-Za-z0-9-]{6,64}$/;

export default function Pair() {
  const { code: codeParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [code, setCode] = useState(codeParam ?? '');
  const [venueId, setVenueId] = useState(null);
  const [tournamentId, setTournamentId] = useState(null);
  const [boardNumber, setBoardNumber] = useState('');

  const [tournaments, setTournaments] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmTakeover, setConfirmTakeover] = useState(false);
  const [done, setDone] = useState(null);

  /**
   * The venues this account may pair a board at. Platform staff administer
   * every venue, so for them the list is unfiltered; everyone else is confined
   * to venues where they hold an admin role.
   */
  const venues = useMemo(() => {
    const all = user?.venues ?? [];
    if (PLATFORM_ROLES.has(user?.platformRole)) return all;
    return all.filter((v) => VENUE_ADMIN_ROLES.has(v.role));
  }, [user]);

  // One venue is not a choice worth making someone tap through. This is the
  // "which venue" step existing precisely when it is ambiguous, and not
  // otherwise.
  useEffect(() => {
    if (venueId === null && venues.length === 1) setVenueId(venues[0].venue_id);
  }, [venues, venueId]);

  // Nights worth offering, once a venue is settled.
  useEffect(() => {
    if (venueId === null) return undefined;

    let live = true;
    setTournaments(null);

    (async () => {
      const results = await Promise.all(
        PAIRABLE_STATUSES.map((status) =>
          apiGet(`/api/venues/${venueId}/tournaments?status=${status}`))
      );

      if (!live) return;

      const rows = results.flatMap((r) => r.data?.tournaments ?? []);
      // Active first: mid-play is overwhelmingly when a board gets paired, and
      // a night still in registration is the less likely of the two.
      rows.sort((a, b) => (a.status === b.status ? 0 : a.status === 'active' ? -1 : 1));

      setTournaments(rows);
      if (rows.length === 1) setTournamentId(rows[0].id);
    })();

    return () => { live = false; };
  }, [venueId]);

  const submit = useCallback(async (takeover) => {
    setBusy(true);
    setError(null);

    const { data, error: err, status } = await apiPost(
      `/api/venues/${venueId}/tournaments/${tournamentId}/pair`,
      { token: code.trim().toUpperCase(), boardNumber: Number(boardNumber), takeover }
    );

    setBusy(false);

    // Not a failure — a question. The board is held by another device and
    // somebody has to decide whether to take it, so this raises the modal
    // rather than a red banner (see pair.js, which passes the flag through
    // from the room for exactly this).
    if (status === 409) {
      setConfirmTakeover(true);
      return;
    }

    setConfirmTakeover(false);

    if (err) {
      setError(err);
      return;
    }

    setDone({ boardNumber: data?.boardNumber ?? Number(boardNumber), takeover });
  }, [venueId, tournamentId, boardNumber, code]);

  // ---- Done -------------------------------------------------------------
  if (done) {
    const venue = venues.find((v) => v.venue_id === venueId);

    return (
      <div className="hd-auth">
        <div className="hd-auth-inner hd-stack">
          <h1 className="hd-section-title" style={{ margin: 0 }}>
            Board {done.boardNumber} paired
          </h1>
          <p className="hd-meta">
            {done.takeover
              ? 'The previous device has been signed out of this board. '
              : ''}
            The tablet{venue ? ` at ${venue.name}` : ''} is loading its scorer now.
          </p>
          <button
            type="button"
            className="hd-btn hd-btn-secondary"
            onClick={() => navigate('/dashboard', { replace: true })}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // ---- No venue this account can pair at --------------------------------
  if (venues.length === 0) {
    return (
      <div className="hd-auth">
        <div className="hd-auth-inner hd-stack">
          <h1 className="hd-section-title" style={{ margin: 0 }}>Pair a board</h1>
          <div className="hd-badge hd-badge-danger" role="alert">
            This account cannot pair boards at any venue.
          </div>
          <p className="hd-meta">
            Pairing needs a venue admin role. Ask whoever runs the venue to add one.
          </p>
        </div>
      </div>
    );
  }

  const codeValid   = TOKEN_PATTERN.test(code.trim());
  const boardValid  = Number.isInteger(Number(boardNumber)) && Number(boardNumber) >= 1;
  const canSubmit   = codeValid && venueId !== null && tournamentId !== null && boardValid;

  return (
    <div className="hd-auth">
      <div className="hd-auth-inner hd-stack">
        <div>
          <h1 className="hd-section-title" style={{ margin: 0 }}>Pair a board</h1>
          <p className="hd-meta">Point a tablet at this venue and board.</p>
        </div>

        {/* Code — prefilled and read-only when it came from a scan. Shown
            either way so what is about to be paired is never invisible. */}
        <div className="hd-stack" style={{ gap: 6 }}>
          <label className="hd-label" htmlFor="pair-code">Device code</label>
          <input
            id="pair-code"
            className="hd-input"
            value={code}
            readOnly={Boolean(codeParam)}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            placeholder="K7M2QX4T"
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
        </div>

        {/* Venue — only when it is genuinely a question. */}
        {venues.length > 1 && (
          <div className="hd-stack" style={{ gap: 6 }}>
            <label className="hd-label" htmlFor="pair-venue">Venue</label>
            <select
              id="pair-venue"
              className="hd-select"
              value={venueId ?? ''}
              onChange={(e) => {
                setVenueId(e.target.value === '' ? null : Number(e.target.value));
                setTournamentId(null);
              }}
            >
              <option value="">Choose a venue…</option>
              {venues.map((v) => (
                <option key={v.venue_id} value={v.venue_id}>{v.name}</option>
              ))}
            </select>
          </div>
        )}

        {venueId !== null && (
          <div className="hd-stack" style={{ gap: 6 }}>
            <label className="hd-label" htmlFor="pair-night">Night</label>
            {tournaments === null ? (
              <div className="hd-empty">Loading…</div>
            ) : tournaments.length === 0 ? (
              <div className="hd-badge hd-badge-danger" role="alert">
                No night is open for pairing at this venue.
              </div>
            ) : (
              <select
                id="pair-night"
                className="hd-select"
                value={tournamentId ?? ''}
                onChange={(e) =>
                  setTournamentId(e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value="">Choose a night…</option>
                {tournaments.map((t) => (
                  <option key={t.id} value={t.id}>
                    {/* Same label AdminTournaments uses — night number, or the
                        id when a standalone tournament has none. */}
                    {t.night_number != null ? `Night ${t.night_number}` : `#${t.id}`}
                    {t.date ? ` · ${formatDateShort(t.date)}` : ''}
                    {t.status === 'registration' ? ' · registration' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {tournamentId !== null && (
          <div className="hd-stack" style={{ gap: 6 }}>
            <label className="hd-label" htmlFor="pair-board">Board number</label>
            <input
              id="pair-board"
              className="hd-input"
              type="number"
              inputMode="numeric"
              min={1}
              value={boardNumber}
              onChange={(e) => setBoardNumber(e.target.value)}
            />
          </div>
        )}

        {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

        <button
          type="button"
          className="hd-btn hd-btn-primary"
          disabled={!canSubmit || busy}
          onClick={() => submit(false)}
        >
          {busy ? 'Pairing…' : 'Pair board'}
        </button>
      </div>

      {confirmTakeover && (
        <div
          className="hd-modal-veil"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busy) setConfirmTakeover(false);
          }}
        >
          <div
            className="hd-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hd-takeover-title"
            tabIndex={-1}
          >
            <div className="hd-modal-head">
              <h2 className="hd-section-title" id="hd-takeover-title" style={{ margin: 0 }}>
                Board {boardNumber} is already paired
              </h2>
            </div>

            <div className="hd-modal-body hd-stack">
              <p style={{ margin: 0 }}>
                This will take over Board {boardNumber}&rsquo;s current session. Are you sure?
              </p>
              {/* Said plainly because it is the consequence someone standing in
                  the room needs: the other tablet stops being able to score,
                  and whoever is holding it finds out when it returns to its
                  pairing screen. */}
              <p className="hd-meta" style={{ margin: 0 }}>
                The device currently on this board will stop scoring immediately and
                return to its pairing screen. Nothing already scored is lost — the new
                tablet picks the match up exactly where it stands.
              </p>
            </div>

            <div className="hd-modal-foot">
              <button
                type="button"
                className="hd-btn hd-btn-quiet"
                disabled={busy}
                onClick={() => setConfirmTakeover(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="hd-btn hd-btn-danger"
                disabled={busy}
                onClick={() => submit(true)}
              >
                {busy ? 'Taking over…' : 'Take over board'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
